import React, { useState } from 'react';

const defaultSettings = {
  workdayStart: '09:00',
  workdayEnd: '18:00',
  graceMinutes: 10,
  halfDayHours: 4,
  overtimeAfterHours: 9,
  autoApproveRegularization: false,
  maxRegularizationsPerMonth: 2,
  requireSelfieOnCheckIn: false,
  enableGeofencing: false,
  geofenceRadiusMeters: 200,
  weekends: ['Saturday', 'Sunday'],
};

const allWeekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AttendanceSettings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const onToggleWeekend = (day) => {
    setSettings((s) => {
      const exists = s.weekends.includes(day);
      const weekends = exists ? s.weekends.filter((d) => d !== day) : [...s.weekends, day];
      return { ...s, weekends };
    });
  };

  const onReset = () => setSettings(defaultSettings);

  const onSave = async () => {
    setSaving(true);
    try {
      // Replace with API call when backend is ready
      // await api.saveAttendanceSettings(settings)
      // Simulate latency
      await new Promise((r) => setTimeout(r, 600));
      // eslint-disable-next-line no-console
      console.log('Attendance settings saved:', settings);
      alert('Attendance settings saved');
    } catch (e) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Attendance Settings</h2>

      {/* Workday and thresholds */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Workday</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Workday Start</span>
            <input
              type="time"
              value={settings.workdayStart}
              onChange={(e) => update('workdayStart', e.target.value)}
              className="rounded-md border px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Workday End</span>
            <input
              type="time"
              value={settings.workdayEnd}
              onChange={(e) => update('workdayEnd', e.target.value)}
              className="rounded-md border px-3 py-2"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Grace Period (minutes)</span>
            <input
              type="number"
              min={0}
              value={settings.graceMinutes}
              onChange={(e) => update('graceMinutes', Number(e.target.value))}
              className="rounded-md border px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Half-day Threshold (hours)</span>
            <input
              type="number"
              min={0}
              step={0.5}
              value={settings.halfDayHours}
              onChange={(e) => update('halfDayHours', Number(e.target.value))}
              className="rounded-md border px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Overtime After (hours)</span>
            <input
              type="number"
              min={0}
              step={0.5}
              value={settings.overtimeAfterHours}
              onChange={(e) => update('overtimeAfterHours', Number(e.target.value))}
              className="rounded-md border px-3 py-2"
            />
          </label>
        </div>
      </div>

      {/* Policies */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Policies</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.autoApproveRegularization}
              onChange={(e) => update('autoApproveRegularization', e.target.checked)}
            />
            <span className="text-gray-700">Auto-approve regularization requests</span>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Max regularizations per month</span>
            <input
              type="number"
              min={0}
              value={settings.maxRegularizationsPerMonth}
              onChange={(e) => update('maxRegularizationsPerMonth', Number(e.target.value))}
              className="rounded-md border px-3 py-2 w-24"
            />
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={settings.requireSelfieOnCheckIn}
              onChange={(e) => update('requireSelfieOnCheckIn', e.target.checked)}
            />
            <span className="text-gray-700">Require selfie on check-in</span>
          </label>
        </div>
      </div>

      {/* Weekends */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Weekends</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {allWeekdays.map((day) => (
            <label key={day} className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.weekends.includes(day)}
                onChange={() => onToggleWeekend(day)}
              />
              <span className="text-gray-700">{day}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Geofencing */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Geofencing</h3>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.enableGeofencing}
            onChange={(e) => update('enableGeofencing', e.target.checked)}
          />
          <span className="text-gray-700">Enable geofencing for check-in/out</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Allowed Radius (meters)</span>
            <input
              type="number"
              min={50}
              step={10}
              value={settings.geofenceRadiusMeters}
              onChange={(e) => update('geofenceRadiusMeters', Number(e.target.value))}
              disabled={!settings.enableGeofencing}
              className="rounded-md border px-3 py-2 w-28 disabled:bg-gray-100"
            />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          className={`px-4 py-2 rounded-md text-white ${saving ? 'bg-primary/70' : 'bg-primary hover:opacity-90'}`}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button
          onClick={onReset}
          disabled={saving}
          className="px-4 py-2 rounded-md border hover:bg-gray-50"
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
};

export default AttendanceSettings;


